import type { ApolloQueryResult } from '@apollo/client'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useCheckPermission } from 'hooks/useCheckPermission'
import {
  type DisabledOptions,
  useDisabledOptions,
} from 'hooks/useDisabledOptions'
import type { EditRowActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/row'
import { useEditWorkflowFieldMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { parseDisabled } from 'utils/parseDisabled'
import ApprovalOptionsFormItems from '../components/ApprovalOptionsFormItems'
import { useFieldUsers } from './useFieldUsers'
import { type EditFieldFormValues, type WorkflowField } from '../types'
import { isApprovalsWorkflowField } from '../utils/isApprovalsWorkflowField'

/**
 * Configures the approval-field edit action and modal.
 */
export function useEdit(
  refetch: () => Promise<ApolloQueryResult<unknown>>
): EditRowActionOptions<WorkflowField, EditFieldFormValues> {
  const isAdmin = useCheckPermission('admin')
  const { fromLabeledValues, toApproverValues } = useFieldUsers()
  const [editField] = useEditWorkflowFieldMutation(DefaultMutationHookOptions)
  const [permissionDisabledOptions] = useDisabledOptions({
    disabled: !isAdmin,
    reason: 'missing permission',
  })
  /**
   * Reuse the values shown by the modal when detecting changes. User-select
   * conversion can reorder approvers, so comparing submitted values directly
   * with the persisted settings could turn an untouched option into an edit.
   */
  const initialValues = (field: WorkflowField): EditFieldFormValues => ({
    options: isApprovalsWorkflowField(field)
      ? field.settings.options
          .filter(({ deleted }) => deleted === null)
          .map(({ approverIds, id, label, tooltip }) => ({
            approverIds: toApproverValues(approverIds),
            label,
            optionId: id,
            tooltip,
          }))
      : [],
  })

  return {
    permission: true,
    disabled: (field) => {
      const approvalsField = isApprovalsWorkflowField(field)
      const disabledOptions: Array<DisabledOptions | undefined> = [
        {
          disabled: !approvalsField,
          reason: {
            why: 'disabled',
            message: "This field's settings aren't editable at this time",
          },
        },
        ...permissionDisabledOptions,
      ]
      const message = parseDisabled(disabledOptions)
      return message ? { message } : false
    },
    /**
     * This form is technically complex enough that we should be using a drawer.
     *
     * In order to re-enforce the idea that this UI is temporary, I'm not going
     * to make it a drawer.
     */
    modal: {
      type: 'simple',
      recordName: 'name',
      body: (field) =>
        isApprovalsWorkflowField(field) && (
          <ApprovalOptionsFormItems field={field} />
        ),
      initialValues,
      onOk: async (field, values) => {
        if (!isApprovalsWorkflowField(field)) return
        const submittedOptionIds = new Set(
          values.options.flatMap(({ optionId }) => optionId ?? [])
        )
        const removeOptionIds = field.settings.options
          .filter(
            ({ deleted, id }) => deleted === null && !submittedOptionIds.has(id)
          )
          .map(({ id }) => id)
        const initialOptionsById = new Map(
          initialValues(field).options.map((o) => [o.optionId, o])
        )
        /**
         * The mutation accepts option deltas. Omitting unchanged active options
         * preserves their audit metadata and prevents them from making an
         * otherwise empty save a field edit.
         */
        const options = values.options
          .map(({ approverIds, label, optionId, tooltip }) => ({
            approverIds: fromLabeledValues(approverIds),
            label,
            optionId,
            tooltip,
          }))
          .filter((option) => {
            if (!option.optionId) return true
            const initialOption = initialOptionsById.get(option.optionId)
            if (!initialOption) return true

            return (
              option.label !== initialOption.label ||
              option.tooltip !== initialOption.tooltip ||
              !arraysHaveSameValues(
                option.approverIds,
                fromLabeledValues(initialOption.approverIds)
              )
            )
          })
        // An empty delta must remain a true no-op, including field audit data.
        if (!options.length && !removeOptionIds.length) return

        await editField({
          variables: { input: { id: field.id, options, removeOptionIds } },
        })
        await refetch()
        simpleEditNotification(field.name)
      },
    },
  }
}

/** Approver order has no semantic meaning. */
function arraysHaveSameValues(left: string[], right: string[]) {
  const sortedLeft = [...left].sort()
  const sortedRight = [...right].sort()

  return (
    sortedLeft.length === sortedRight.length &&
    sortedLeft.every((value, index) => value === sortedRight[index])
  )
}
